import { FC, Fragment, useEffect, useRef } from 'react';
// Hooks
import useNavigateTo from 'hooks/useNavigateTo';
import useQuestionInterval from 'hooks/useQuestionInterval';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setQuestionReady, resetQuestionStatuses, setQuestionIndex } from 'store/player/playerSlice';
import { selectQuestion, selectQuiz } from 'store/quiz/quizSelectors';
import { selectQuestionIndex, selectQuestionEnd, selectFilteredAnswersOptions } from 'store/player/playerSelectors';
import QuizTypes from 'types/QuizTypes';
import IQuiz from 'models/Quiz';
// Components
import AppBar from 'components/AppBar';
import QuestionMedia from './QuestionMedia';
import QuestionScoringBar from './QuestionScoringBar';
import QuestionPersonalityBar from './QuestionPersonalityBar';
import Question from './Question';
import ScoringAnswers from './ScoringAnswers';
import PersonalityAnswers from './PersonalityAnswers';
import PoweredBy from 'components/PoweredBy';

const QuestionPage:FC = () => {
  const dispatch = useAppDispatch();
  // State
  const quiz:IQuiz = useAppSelector(selectQuiz);
  const question = useAppSelector(selectQuestion);
  const questionIndex = useAppSelector(selectQuestionIndex);
  const questionEnd:boolean = useAppSelector(selectQuestionEnd);
  const flteredAnswersOptions = useAppSelector(selectFilteredAnswersOptions);

  const navigateTo = useNavigateTo();
  const { score, width, time } = useQuestionInterval(question.score);

  const timeout = useRef<any>(null);
  const timeoutTime = 2000;

  const isPollQuizzes = [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quiz.type);
  const scoreVisible = quiz.type === QuizTypes.Scoring;

  useEffect(() => {
    if (quiz.type === QuizTypes.RankingPoll && !flteredAnswersOptions.length) {
      navigateTo('register');
      return;
    }
    
    if (!question.image && !question.video && !question.audio) dispatch(setQuestionReady(true));

    return () => {
      if ( timeout.current ) clearTimeout(timeout.current);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if ( questionEnd ) {
      timeout.current = setTimeout(() => {
        dispatch(resetQuestionStatuses());
        dispatch(setQuestionIndex(questionIndex + 1));
        navigateTo('pages-handler');
      }, timeoutTime);
    }
    // eslint-disable-next-line
  }, [questionEnd]);

  return (
    <div
      className="flex-grow flex flex-col"
      style={{ background: quiz.backgroundImage ? `url('${quiz.backgroundImage}') center / cover` : '' }}
    >
      <div className="p-4">
        <AppBar scoreVisible={scoreVisible} />
      </div>
      <div
        className="
          relative
          flex justify-center items-center
          overflow-hidden
        "
      >
        <QuestionMedia />
        {!isPollQuizzes ? (
          <Fragment>
            <QuestionScoringBar score={score} width={width} />
            <QuestionPersonalityBar />
          </Fragment>
        ) : null}
      </div>
      <div style={{
        flex: 1,
        fontFamily: quiz.questionsSettings.font ? `${quiz.questionsSettings.font}, sans-serif` : 'initial'
      }}>
        <Question />
        <PersonalityAnswers />
        <ScoringAnswers score={score} time={time} />
      </div>
      <PoweredBy />
    </div>
  )
}

export default QuestionPage;
